/* ****************************************************************************
 * Tag Cloud visualization
 * ***************************************************************************/
import _ from 'lodash';
import * as d3 from 'd3c';
import d3util from 'utils/d3-wl-util';
import getDummyData from 'utils/getDummyData';
import Sentiment from 'Sentiment';
import { calculcateTagLimitByWidthAndHeight } from 'components/weblyzard/tagcloud/p-tagcloud';

// chart template inspired by http://bost.ocks.org/mike/chart/
// and http://bost.ocks.org/mike/chart/time-series-chart.js
export default function(params) {
	const defaultOptions = {
		updateColor: '#a8a8a8',
		temporalAnimationDuration: 800,
		transitionDuration: 125
	};
	const options = _.merge(defaultOptions, params);
	return function() {
		// Used for temporal animations
		let isTagsFixed = false;
		let chart = null;

		const _this = this;
		// default values
		// to add options to override them implement getter/setter methods
		// like for autoplay/width/height
		let width = null;
		let height = null;

		// if tagcloud is actually visible
		let visible = true;

		// holds the actual keyword set
		let tags = [];

		// lookup hash/table for keyword ids
		const keywordsHash = [];

		// zoom limits
		let scaleExtent = [1, 4];

		// defaults tagcloud to centered lines
		let justify = false;

		// need for auto-zoom transition between tagcloud states
		let previousFactor = null;

		// limit offers different ways to limit the amount of tags
		// * false : no limit
		// * 'auto' : automatically limits amount of keywords to retain readybility of individual keywords
		// * integer, e.g. 50 : statically sets the amount of keywords
		// * percent, e.g. '50%' : limits amount of keywords by given percentage
		let limit = false;

		// default DOM wrapper element
		let wrapper = '#wrapper';

		// for highlighting individual keywords
		// setter method expects an array of keywords
		let highlight = false;

		// maximum occurence count of a all keywords
		// needed to normalize font sizes
		let maxCount = 1;

		let svg = {};

		// let's you run the layout algorithm even if the keyword set didn't change
		// useful if wrapper/canvas size changes
		let forceRedraw = false;

		// indicator if the tagcloud is currently being updated
		// needed to track states of transitions
		let working = false;

		//used to prevent redrawing of tagcloud layout while user is zooming or dragging
		let currentlyZooming = false;

		// stores additionaly submitted keyword set while the tagcloud is being updated
		let queue = [];

		// always use a prefix for keyword ids to avoid reserved words as
		// javascript keys or DOM ids
		const idPrefix = 'tag_';

		let updating = true;
		let leaveGreyedOut = false;
		let _neutral_topic_color = options.neutralColor ? options.neutralColor : 'rgb(204, 204, 204)';
		let _punctuationMark = "";

		let colorMode = 'sentiment';

		//variables needed for zoom behaviour
		let destinationX = 0;
		let destinationY = 0;
		let trueWidth = 340;
		let trueHeight = 208;
		let disableZoom = false;

		const zoomedEvent = function() {
			const {x, y, k} = d3.event.transform;

			d3
				.select(`${wrapper} .zoomer`)
				.attr('transform', `translate(${x},${y}) scale(${k})`);
		};

		// initialize zoom behavior
		const zoom = d3
			.zoom()
			.on('zoom', zoomedEvent)
			.scaleExtent(scaleExtent);
		zoom.on('start', () => (currentlyZooming = true));
		zoom.on('end', () => (currentlyZooming = false));

		const setMinZoom = function(zoom, minValue) {
			if (disableZoom) {
				scaleExtent = [minValue, minValue];
			} else {
				scaleExtent = [minValue, 4];
			}
			zoom.scaleExtent(scaleExtent);
		};

		const setTranslateExtent = function(offsetHeight, offsetWidth) {
			zoom.translateExtent([[offsetWidth, -offsetHeight], [trueWidth + offsetWidth, trueHeight - offsetHeight]]);
		};

		const getWrapperBoundaries = function() {
			return d3
				.select(wrapper)
				.node()
				.getBoundingClientRect();
		}

		const sentimentColor = function(sentiment) {
			return Sentiment.getColor(sentiment, 'global');
		};

		const getColor = function(tag) {
			if (colorMode !== 'sentiment') {
				return tag.topic === null ? _neutral_topic_color /* @topic-color-neutral */ : tag.topic.color;
			} else {
				return sentimentColor(tag.sentiment);
			}
		};

		function noData() {
			working = false;
			tags = [];
			if (chart) {
				chart.showPlaceholder();
			}
		}

		function createSVGelement() {
			//create the skeletal chart if it does not exist
			svg = d3.select(wrapper).select('svg.tagCloud');

			if (svg.empty()) {
				svg = d3
					.select(wrapper)
					.append('svg')
					.classed('tagCloud', true)
					.attr('pointer-events', 'all')
					.call(zoom);
			}
		}

		function removeElementContent(container) {
			container
				.selectAll('*')
				.remove();
		}

		function showPlaceholderTags(container, width, height) {
			const marginH = width / 17; // left/right padding
			const marginV = height / 10.4; // top/bottom padding
			const tagHeight = Math.max(6, (height - marginV * 2) / 30); // 6 is @placeholder-rect-height
			const tagLines = Math.round((height - marginV * 2) / (tagHeight * 4)); // lines of tags
			const paddingH = marginH; // horizontal padding between tags
			const paddingV = (height - marginV * 2 - tagHeight * tagLines) / (tagLines - 1); // vertical padding between tags

			// create dummy data
			const dummyData = getDummyData(
				'tagcloud',
				{
					width,
					tagLines,
					marginH,
					tagHeight,
					paddingH
				}
			);

			removeElementContent(container);
			container
				.attr('width', width)
				.attr('height', height);

			const tagGroup = container
				.append('g')
				.attr('transform', `translate(${width / 2},${height / 2})`);

			tagGroup
				.selectAll('rect')
				.data(dummyData, function(d) {
					return `${d.linenumber}_${d.tagnumber}`;
				})
				.enter()
				.append('rect')
				.attr('class', 'placeholderTag')
				.attr('width', function(d) {
					return d.width;
				})
				.attr('height', function(d) {
					return d.height;
				})
				.attr('x', function(d) {
					return -width / 2 + marginH + d.width * d.tagnumber + paddingH * d.tagnumber;
				})
				.attr('y', function(d) {
					return -height / 2 + marginV + d.height * d.linenumber + paddingV * d.linenumber;
				});
		}

		function getActualLimit(limit, data) {
			// if limit is positive integer use that value directly
			const intRegex = /^\d+$/;
			if (intRegex.test(limit)) {
				return parseInt(limit, 10);
			}
			// check if limit is percentage
			const percentageRegex = /^\d+%$/;
			if (percentageRegex.test(limit)) {
				return Math.round((parseInt(limit.replace('%', ''), 10) / 100) * data.length);
			}
			if (limit === 'auto') {
				return calculcateTagLimitByWidthAndHeight([width, height]);
			}

			return 9999;
		}

		function prepareData(data) {
			maxCount = 0; //reset maxcount

			let totalLimit = 9999;
			if (limit) {
				totalLimit = getActualLimit(limit, data);
			}

			const retdata = JSON.parse(JSON.stringify(data));

			retdata.forEach(tag => {
				tag.color = getColor(tag);
				if (tag.count > maxCount) {
					maxCount = tag.count;
				}
			});

			return retdata.slice(0, totalLimit);
		}

		function setTags(data) {
			const tagsHash = {};
			const newTagsHash = [];
			let deletes = 0;
			let sameTagSet = false;
			let item, n;
			const firstRun = tags.length === 0 || data.length === 0 ? true : false;

			// keep only tags which are shared by the new dataset
			const nt = [];
			for (let i = 0; i < tags.length; ++i) {
				n = tags[i];
				let deleteMe = true;
				for (let k = 0; k < data.length; ++k) {
					item = data[k];
					if (item.id.toLowerCase() === n.id.toLowerCase()) {
						deleteMe = false;
					}
				}
				if (deleteMe === false) {
					nt.push(tags[i]);
				} else {
					deletes++;
				}
			}
			tags = nt;

			// add new tags, update existing ones
			for (let i = 0; i < data.length; ++i) {
				item = data[i];
				let findItem = false;
				for (let j = 0; j < tags.length; ++j) {
					n = tags[j];
					if (item.id.toLowerCase() === n.id.toLowerCase()) {
						tagsHash[n.id.toLowerCase()] = n;
						n.count = item.count;
						n.sentiment = item.sentiment;
						n.color = item.color;
						findItem = true;
					}
				}
				if (!findItem) {
					tagsHash[item.id.toLowerCase()] = item;
					newTagsHash.push(item.id.toLowerCase());
					// prefix to circumvent reserved names like 'length'
					keywordsHash[idPrefix + item.displayName.toLowerCase()] = item;
					tags.push(item);
				}
			}

			tags.sort(( a, b ) => {
				if ( a.displayName < b.displayName ){
				  return -1;
				}
				if ( a.displayName > b.displayName ){
				  return 1;
				}
				return 0;
			  });

			_.forEach(tags, function(d, tagIndex) {
				// add desaturated update color to the data
				const hslColor = d3.hsl(d.color);
				if (isNaN(hslColor.h)) {
					hslColor.h = 0; // process NaN hue values
				}
				// if the original color is already desaturated (gray color shades), use default update color; otherwise, desaturate the original color
				d.updateColor = hslColor.s === 0 ? options.updateColor : d3.rgb(d3.hsl(hslColor.h, 0, hslColor.l)).toString();

				if (_punctuationMark !== "" && d.displayName.includes(" ")) {
					const dashIndex = d.displayName.indexOf(" ");
					const firstTerm = d.displayName.substring(0, dashIndex);
					const tagExists = tags.find((x, findIndex) => {
						let toCompare = "";
					    if(x.displayName.includes(" ")) {
							const index = x.displayName.indexOf(" ");
							toCompare = x.displayName.substring(0, index);
						} else {
							toCompare = x.displayName;
						}
						return toCompare.startsWith(firstTerm) &&
							toCompare.endsWith(firstTerm) &&
							d.id !== x.id &&
							tagIndex > findIndex;
					});
					if (tagExists) {
						const restOfTerm = d.displayName.substring(dashIndex + 1);
						d.shortenedName = `${_punctuationMark}${restOfTerm}`;
					} else {
						d.shortenedName = d.displayName;
					}
				} else {
					d.shortenedName = d.displayName;
				}
			});

			// placement
			for (let i = 0; i < newTagsHash.length; ++i) {
				n = tagsHash[newTagsHash[i]];
				// default inital placement
				n.x = n.y = n.px = n.py = 0;
			}

			if (!firstRun && newTagsHash.length === 0 && deletes === 0) {
				sameTagSet = true;
			}

			return sameTagSet;
		}

		const updateDisplay = function(tagupdate, sameTagSet) {
			const tag = tagupdate;
			const zoomer = `${wrapper} .zoomer`;
			const svgElement = d3.select(`${wrapper} svg`);

			const tagMap = {};
			const currentTags = [];
			const textElements = tagupdate.selectAll(`${wrapper} .tagText`);
				
			textElements.style('font-size', function(d, i) {
				const that = d3.select(this);
				const displayName = that.datum().displayName.toLowerCase();
				currentTags.push(displayName);
				var self = this;
				tagMap[that.datum().displayName.toLowerCase()] = self;
				return `${10 + (d.count / maxCount) * 10}px`;
			});

			currentTags.sort();

			// this function finalizes the tagcloud state
			// * it gets called after all necessary transitions got called
			// * it does the final transparency settings for each keyword
			// * it checks the queue if a new keyword set got submitted
			// * if everythings done, 'working' is set to false to allow for new updates
			const consolidate = function() {
				const highlightTransitionDelay = !sameTagSet || forceRedraw ? options.transitionDuration : 0;
				tag
					.transition('tagcloud.tag.final')
					.duration(options.transitionDuration)
					.delay(highlightTransitionDelay)
					.style('opacity', 1)
					.each(function(d) {
						if (highlight) {
							return d3
								.select(this)
								.select('text')
								.classed('highlight', highlight.indexOf(d.displayName) >= 0);
						}

						return d3
							.select(this)
							.select('text')
							.classed('highlight', false);
					});

				// check if additional keyword-sets were submitted
				// while we were updating the tagcloud
				if (queue.length > 0) {
					// if there are keyword-sets in the queue,
					// get the most recent one and reset the queue
					const keywords = queue.pop();
					queue = [];
					// update the tagcloud using the new keyword-set
					chart.update(keywords);
				} else {
					// if there's nothing in the queue
					// we're finished!
					working = false;
					// reset forceRedraw
					forceRedraw = false;
				}
			};

			const doTransitions = function() {
				// move tags to their new positions using a transition
				tag
					.transition('tagcloud.tag.reposition')
					.duration(options.transitionDuration)
					.attr('transform', function(d) {
						return `translate(${d.x},${d.y})`;
					});

				textElements
					.transition('tagcloud.text.updatecolor')
					.style('fill', function(d) {
						return updating ? d.updateColor : d.color;
					})
					.on('end', function() {
						d3
							.select(this)
							.style('pointer-events', updating ? 'none' : 'all');
					});
			};

			const lineHeight = 20;
			let scaleFactor;

			const doRepositioning = function(yOffset, destinationX, destinationY, scaleFactor, adjustedX) {
				// if tags have changed do transition for new zoom/position
				svgElement
					.transition()
					.duration(options.transitionDuration)
					.call(zoom.transform, d3.zoomIdentity.translate(destinationX, destinationY).scale(scaleFactor))
					.on('end', function() {
						consolidate();
						setTranslateExtent(yOffset, adjustedX);
					});
				};

			const scaleFactorFactory = function(w, h) {
				return () => (w / width > h / height ? (width / w) * 0.95 : (height / h) * 0.95);
			};

			let adjustedX = 0;

			// hotfix: try to avoid the bug which causes new tags to be stacked
			// in upper right corner
			//PV: trying without hotfix
			//sameTagSet = false;
			//forceRedraw = true;
			// tagcloud layout
			if (sameTagSet && !forceRedraw) {
				consolidate();
			} else {
				// for easier calculations we set zoom to 1 and remember the previous state
				// to return to it later
				previousFactor = d3.zoomTransform(d3.select(`${wrapper} svg`).node());

				setMinZoom(zoom, 1);
				svgElement.call(zoom.scaleTo, 1);

				// initialize padding and line-height
				const wordPadding = 10;

				// returns width of a given tag including word padding
				const tagWidth = function(tag) {
					// we used tagMap[tag].getBoundingClientRect().width earlier
					// but there were word-wrapping issues in IE9 due to rounding issues
					return tagMap[tag].getBBox().width + wordPadding;
				};

				// returns height of a given tag
				const tagBBox = function(tag) {
					return tagMap[tag].getBBox();
				};

				// returns width of all tags combined including word padding
				const tagsWidth = function(tags) {
					if (tags.length === 0) {
						return 0;
					}

					return tags.reduce(function(a, b) {
						if (typeof a === 'string') {
							a = tagWidth(a);
						}
						return a + tagWidth(b);
					});
				};

				const getActualLineHeight = function(lineHash) {
					let yOffset = { height: 0, y: 0 };
					for (let i = 0; i < lineHash.length; i++) {
						const tagBbox = tagBBox(lineHash[i]);

						if (!yOffset || tagBbox.height > yOffset.height) {
							yOffset = tagBbox;
						}
					}

					return {
						y: yOffset.y,
						height: yOffset.height
					};
				};

				// this would be the ideal line-width to place all tags in an area
				// with the same aspect ratio as the wrapper element
				// the factor at the end is used to approximately accomodate white space
				// introduced by word wrapping. otherwise the tagcloud would always be higher than wide
				let lineWidth = (
					tagsWidth(currentTags) / Math.sqrt(tagsWidth(currentTags) / (lineHeight / (height / width)))
				) * 1.07;
				if (isNaN(lineWidth)) {
					lineWidth = width;
				}

				//maximum number of times a new layout is tried. In most cases a satisfying layout should be found in less attempts
				const maxIteration = 25;

				const layout = function(lineWidth, layouts, iteration = 0) {
					adjustedX = 0;
					// we use lineHash to store tags of the current line so we can adjust
					// the layout (centered or justified) of the line when it's full
					let lineHash = [];

					// as we iterate over the tags we store the currentLineWidth of lineHash and check against
					// lineWidth to see if the current line is full
					let currentLineWidth = 0;

					// once a line is full we run adjustLine to layout the line either centered or justified
					const adjustLine = function(lineWidth, currentLineWidth) {
						const margin = (lineWidth - currentLineWidth) / 2;
						for (let l = 0; l < lineHash.length; ++l) {
							const rTag = lineHash[l];
							const r = keywordsHash[idPrefix + rTag];
							if (justify) {
								r.x += lineHash.length > 1 ? (margin / (lineHash.length - 1)) * l * 2 : margin;
							} else {
								r.x += margin;
							}
							adjustedX = Math.min(adjustedX, r.x);
						}
					};

					// pos is used to track the current position in the tagcloud as we iterate over the tags
					let pos = {
						x: 0,
						y: 0
					};

					// the actual layout process
					// we iterate over all tags and adjust their positions
					let lines = 0;
					const wordsPerLine = [];
					const lineOffsets = {};
					for (let t = 0; t < currentTags.length; ++t) {
						const currentTagWidth = tagWidth(currentTags[t]);

						const currentTagPosition = {
							x: pos.x,
							y: pos.y
						};
						const k = keywordsHash[idPrefix + currentTags[t]];

						// if we haven't reached lineWidth we add the current tag to the current line
						if (pos.x + currentTagWidth <= lineWidth) {
							lineHash.push(currentTags[t]);
							currentLineWidth += currentTagWidth;
							// if we have reached lineWidth we move the current tag to the next line
							// and adjust the layout of the current line (centered or justified)
						} else {
							if (!lineOffsets.firstLine) {
								lineOffsets.firstLine = getActualLineHeight(lineHash);
							}
							lines++;
							adjustLine(lineWidth, currentLineWidth);
							currentTagPosition.x = 0;
							currentTagPosition.y += lineHeight;
							currentLineWidth = currentTagWidth;
							wordsPerLine.push(lineHash.length);
							lineHash = [];
							lineHash.push(currentTags[t]);
						}

						k.x = currentTagPosition.x;
						k.y = currentTagPosition.y;

						pos = currentTagPosition;
						pos.x += currentTagWidth;
					}

					if (lineHash.length > 0) {
						// adjust the last line
						adjustLine(lineWidth, currentLineWidth);
						lines++;
						lineOffsets.lastLine = getActualLineHeight(lineHash);
					}
					// If the last line contains just a few terms
					// we set a new lineWidth and calculate another
					// iteration of the tagcloud layout to get a
					// prettier result. The iteration trigger gets fired
					// when the last line has fewer terms than 2/3 of
					// the mean of the term frequency of the other lines
					const wordsPerLineMean = wordsPerLine.length > 0
						? wordsPerLine
							.reduce(function(a, b) {
									return a + b;
							}) / wordsPerLine.length
						: 0;
					const satisfyingLayout = false;
					iteration++;

					// for now never return a satisfaying layout but always collect a number of options and take best option out of them.
					// if (lineHash.length > minWordsPerLine && lineWidth > 900) {
					// 	satisfyingLayout = true;
					// }
					// if layout not satisfying try another time with smaller linewidth; maximum of 5 tries
					if (!satisfyingLayout && iteration < maxIteration) {
						const aproxFactor = 0.995;
						//if (iteration > 4) aproxFactor = 0.97;
						//if (iteration > 8) aproxFactor = 0.96;
						layout(lineWidth * aproxFactor, layouts, iteration);
					}

					// the updated coordinates for the keywords
					// are already in the corresponding objects
					// here we just return the relevant data
					// to be able to adjust the viewport
					layouts.push({
						lines,
						wordsPerLine,
						wordsPerLineMean,
						satisfyingLayout,
						wordsLastLine: lineHash.length,
						width: lineWidth,
						height: lines * lineHeight,
						offset: lineOffsets
					});
					return layouts;
				};

				const tagLayouts = layout(lineWidth, []);
				let tagLayout = tagLayouts[0]; //index 0 because last iteration of layout function pushes first to layouts array

				// if we were not able to return a satisfying (=enough words in last line) layout
				// we pick the best among the already generated ones
				// trade-off between how much words in the last line we want and how large the tagcloud should be in terms of width
				if (!tagLayout.satisfyingLayout) {
					//get a nested array, key is the number of words in the last line and sort the values in regard to the highest width
					const widthGrouping = d3
						.nest()
						.key(x => x.wordsLastLine)
						.sortValues((x, y) => {
							const k = scaleFactorFactory(y.width, y.height)();
							return y.width * k - x.width * k;
						})
						.entries(tagLayouts);

					const mostWordsLastLine = Math.max(...widthGrouping.map(x => Number(x.key)));
					const minWordsLastLineRequired = 2;

					//try to avoid last lines with only 1 or 2 words. Apart from that take the layout with biggest width
					if (mostWordsLastLine > minWordsLastLineRequired) {
						tagLayout = widthGrouping
							.filter(x => Number(x.key) > minWordsLastLineRequired)
							.reduce((widest, x) => {
								const candidate = x.value ? x.value[0] : x.values[0]; //random d3 behavior???
								return candidate.width > widest.width ? candidate : widest;
							}, tagLayout);
					} else {
						const candidate = widthGrouping.find(x => x.key === mostWordsLastLine.toString());
						tagLayout = candidate.value ? candidate.value[0] : candidate.values[0];
					}

					// regenerate the coordinates of the keywords of the given layout;
					lineWidth = tagLayout.width; //pv: in earlier version width was multiplied by 0.99 but this increased the likelihood for the last line problem
					//lineHeight = tagLayout.height / tagLayout.lines; //pv: line seems obsolete since lineHeight is never changed
					tagLayout = layout(lineWidth, [], maxIteration).pop();
				}

				// move tags to their new positions so we can determine the bounding box for the later auto-pan+zoom
				const previousPositions = [];

				tag.attr('transform', function(d, i) {
					//the new zoomTransform is not working here since no d3-zoom is attached to the single tags, only to the main svg
					const currentPos = d3util.transform(d3.select(this).attr('transform'));

					if (currentPos !== null) {
						previousPositions[i] = {
							x: currentPos.translateX,
							y: currentPos.translateY
						};
					} else {
						previousPositions[i] = {
							x: 0,
							y: 0
						};
					}

					return `translate(${d.x},${d.y})`;
				});

				// get the bounding boxes so we know where to translate to later on
				const we = d3
					.select(wrapper)
					.node()
					.getBoundingClientRect();
				const ge = d3
					.select(zoomer)
					.node()
					.getBoundingClientRect();
				trueWidth = ge.width;
				trueHeight = ge.height;

				const scaleFactor = scaleFactorFactory(trueWidth, trueHeight)();

				//set zoom to scaleFactor in order to calculate x and y position correctly
				setMinZoom(zoom, scaleFactor);
				svgElement.call(zoom.scaleTo, scaleFactor);
				//trying to simplify position calculation, see how it works in practice
				const marginX = (we.width - trueWidth * scaleFactor) / 2;
				const marginY = (we.height - trueHeight * scaleFactor) / 2;
				//add height of first line to top margin and subract descent of last line
				const firstLineOffset = tagLayout && tagLayout.offset && tagLayout.offset.firstLine ? tagLayout.offset.firstLine.height : 0;
				const offsetY = firstLineOffset - (tagLayout.offset.lastLine.y + tagLayout.offset.lastLine.height);
				destinationX = marginX + Math.abs(adjustedX) * scaleFactor;
				destinationY = marginY + offsetY * scaleFactor;

				//set zoom to initial position for the animation afterwards
				svgElement.call(
					zoom.transform,
					d3.zoomIdentity.translate(previousFactor.x, previousFactor.y).scale(previousFactor.k)
				);

				// move tags back to their previous positions so we can do the animation afterwards
				tag.attr('transform', function(d, i) {
					return `translate(${previousPositions[i].x},${previousPositions[i].y})`;
				});

				textElements.interrupt('tagcloud.text.greyout');
				tag
					.exit()
					.remove();

				doTransitions();
				doRepositioning(offsetY, destinationX, destinationY, scaleFactor, adjustedX);
			}
		};

		const updateData = function(data, sameTagSet) {
			createSVGelement();

			//not really the d3 way to solve it, but the tag removal for placeholder conflicted with the internal d3 join
			if (svg.selectAll('g').empty()) {
				svg
					.append('svg:g')
					.attr('class', 'zoomer')
					.attr('width', width)
					.attr('height', height)
					.append('svg:g')
					//.attr('width', width)
					//.attr('height', height)
					.attr('class', 'canvas');
			}

			svg
				.attr('width', width)
				.attr('height', height)
				.data([data]);

			const g = d3.select(`${wrapper} .canvas`);
			const tag = g.selectAll('.tag');

			let tagupdate = tag
				.data(tags, function(d) {
					return d.id;
				})

			tagupdate.select("text").text(function(d) {
				return d.shortenedName.toLowerCase();
			});

			// new elements
			const tagInner = tagupdate
				.enter()
				.append('g');

			tagupdate
				.select('.tagText')
				.style('font-size', function(d) {
					return `${10 + (d.count / maxCount) * 10}px`;
				});

			tagInner
				.attr('id', d => `tagcloud_${d.id}`)
				.attr('transform', function(d) {
					return `translate(${d.x},${d.y})`;
				})
				.attr('class', 'istag tag')
				.style('fill', function(d) {
					return d.color;
				})
				.style('opacity', 0);

			tagInner
				.append('text')
				.attr('class', 'tagText')
				.style('font-size', function(d) {
					return `${10 + (d.count / maxCount) * 10}px`;
				})
				.text(function(d) {
					return d.shortenedName.toLowerCase();
				})
				.on('click', chart.click)
				.on('mouseenter', chart.hoverEnter)
				.on('mousemove', chart.hoverMove)
				.on('mouseleave', chart.hoverEnd);

			//PV: I removed the transition, because it led to some timing problems when tagcloud is forced to redraw multiple times in short timespan
			//(when selecting topics quickly or when changing different maps in configuration modal)
			tagupdate
				.exit()
				.remove();
			tagupdate = tagInner.merge(tagupdate);
			updateDisplay(tagupdate, sameTagSet);
		};

		function ensureIdentifiers(data) {
			//in dashboard a object is returned so this statements should not be executed in dashboard version.
			if (Array.isArray(data)) {
				data.forEach(elem => {
					if (elem.id && !elem.name) {
						elem.name = elem.id;
					}

					if (elem.name) {
						if (!elem.id) {
							elem.id = elem.name;
						}
						if (!elem.displayName) {
							elem.displayName = elem.name;
						}
					} else if (elem.displayName) {
						if (!elem.name) {
							elem.name = elem.displayName;
						}
						if (!elem.id) {
							elem.id = elem.displayName;
						}
					} else {
						return;
					}
				});
			}

			return data;
		}

		// main chart function
		chart = function(selection) {
			selection.each(function(data) {
				working = true;
				// ensuring right data structure
				if (!data) {
					noData();
					return;
				}
				data = ensureIdentifiers(data);
				if (!data) {
					noData();
					return;
				}
				data = prepareData(data); // belongs to the store
				if (data.length === 0) {
					noData();
					return;
				}

				// if placeholder was active delete the content of svg
				if (tags.length === 0) {
					removeElementContent(d3.select('svg.tagCloud'));
				}

				// leave tags that are available in new search; method is setting tags variable as side effect
				const sameTagSet = setTags(data);

				// update data
				updateData(data, sameTagSet);
			});
		};

		chart.width = function(value) {
			if (value === undefined) {
				return width;
			}
			width = value;
			if ('attr' in svg) {
				svg.attr('width', width);
			}
			return chart;
		};

		chart.height = function(value) {
			if (value === undefined) {
				return height;
			}
			height = value;
			if ('attr' in svg) {
				svg.attr('height', height);
			}
			return chart;
		};

		chart.resize = function() {
			const dim = getWrapperBoundaries();
			chart.width(dim.width)
				.height(dim.height);
			return chart;
		};

		chart.justify = function(value) {
			if (value === undefined) {
				return justify;
			}
			justify = value;
			return chart;
		};

		chart.limit = function(value) {
			if (value === undefined) {
				return limit;
			}
			limit = value;
			return chart;
		};

		chart.highlight = function(value) {
			if (value === undefined) {
				return highlight;
			}
			highlight = value;
			if (highlight && highlight.length === 0) {
				highlight = false;
			}
			return chart;
		};

		chart.wrapper = function(value) {
			if (value === undefined) {
				return wrapper;
			}
			wrapper = value;
			createSVGelement();
			return chart;
		};

		chart.showPlaceholder = function() {
			showPlaceholderTags(d3.select('svg.tagCloud'), width, height);
			return chart;
		};

		chart.forceRedraw = function(value) {
			if (value === undefined) {
				return forceRedraw;
			}
			forceRedraw = value;
			return chart;
		};

		chart.working = function() {
			return working;
		};

		chart.leaveGreyedOut = function() {
			leaveGreyedOut = true;
			return chart;
		};

		chart.queue = function(value) {
			if (value === undefined) {
				return queue;
			}
			queue.push(value);
			return chart;
		};

		chart.click = function(d) {
			d3.event.stopPropagation();
			// show the tooltip but fix it by telling it that the point was clicked
			d.element = this;
			_this.observer.notify('click', d3);
		};

		chart.hoverEnter = function() {
			// this will just call the showCallback if no tooltip is fixed
			_this.observer.notify('mouseover', d3);
		};

		chart.hoverMove = function(d) {
			d.element = this;
			_this.observer.notify('mousemove', d3);
		};

		chart.hoverEnd = function(d) {
			d.element = this;
			_this.observer.notify('mouseout', d3);
		};

		// define the function for callback on hiding
		chart.resetHover = function(element) {
			if (element) {
				d3
					.select(element)
					.classed('highlight', false);
			} else {
				// reset all highlighted tags
				d3
					.select(wrapper)
					.selectAll('text')
					.classed('highlight', false);
			}
		};

		chart.resetHighlight = function() {
			let datum;
			highlight = false;

			if (!visible || isTagsFixed) {
				return;
			}

			// if the tagcloud is still occupied with e.g. transitions
			// we queue the latest data again to trigger another update
			// which will take the highlight reset into account,
			// otherwise we just trigger another update
			if (working || currentlyZooming) {
				datum = chart.queue().pop();
				if (typeof datum === 'undefined') {
					datum = d3.select(wrapper).datum();
				}
				chart.queue(datum);
			} else {
				d3
					.select(wrapper)
					.call(chart);
			}
		};

		chart.update = function(data) {
			if(leaveGreyedOut) {
				leaveGreyedOut = false;
			} else {
				updating = false;
			}
			isTagsFixed = false;

			d3.select(wrapper).selectAll('.tagText').interrupt().style('opacity', '1');

			if(!width || !height) {
				const dim = getWrapperBoundaries();
				if (dim.width !== 0 && dim.height !== 0) {
					chart
						.width(dim.width)
						.height(dim.height);
				}
			}
			if(!width || !height) {
				return;
			}

			visible = true;

			if (!d3.select(wrapper).selectAll('.placeholderTag').empty()) {
				noData(); // if the placeholder tags are displayed, update them
			}

			const activeTopics = Array.isArray(data) ? data.length > 0 :  _.some(
				data,
				function(d) {
					return d.activated && d.keywords.length;
				}
			);

			if (data === undefined) {
				d3
					.select(wrapper)
					.call(chart);
			} else {
				d3
					.select(wrapper)
					.datum(!activeTopics ? [] : data)
					.call(chart);
			}
		};

		// Update with fixed tag position. Change only size / color
		chart.updateFixed = function(data, animationCallback) {
			isTagsFixed = true;
			const dataSelection = svg
				.selectAll('.tag')
				.data(data.filter(d => !svg.selectAll(`#tagcloud_${d.id}`).empty()), el => el.id);

			dataSelection
				.enter()
				.merge(dataSelection)
				.each(function(d, i) {
					const transition = d3.select(this).select('.tagText')
						.transition()
						.duration(options.temporalAnimationDuration)
						.ease(d3.easeLinear)
						.style('fill', getColor(d))
						.style('opacity', 1);
					animationCallback && i === 0 && transition.filter((_t, tIndex) => tIndex === 0).on('end', () => {
						if (isTagsFixed) {
							animationCallback();
						} else {
							dataSelection.exit().style('opacity', 1);
						}
					});
				});

			dataSelection
				.exit()
				.select('.tagText')
				.transition()
				.duration(options.temporalAnimationDuration)
				.ease(d3.easeLinear)
				.style('opacity', 0);

			if (dataSelection.enter().merge(dataSelection).size() === 0) {
				setTimeout(animationCallback, options.temporalAnimationDuration);
			}
		};

		chart.visible = function(value) {
			if (value === undefined) {
				return visible;
			}
			visible = value;
			return chart;
		};

		chart.redraw = function() {
			d3
				.select(wrapper)
				.call(chart);
		};

		chart.setColorMode = function(value) {
			colorMode = value;
			return chart;
		};

		chart.disableZoom = function() {
			disableZoom = true;
			return chart;
		};

		chart.neutralTopicColor = function(color) {
			if (!arguments.length || typeof color !== "string") {
				return chart;
			}
			_neutral_topic_color = color;
			return chart;
		};

		chart.punctuationMark = function(mark) {
			if (!arguments.length || typeof mark !== "string") {
				_punctuationMark = "";
				return chart;
			}
			_punctuationMark = mark;
			return chart;
		};

		chart.greyOutElements = function() {
			//selectAll throwing an exception, see situation after implementation of topicselectioncontroller
			try {
				if (svg === undefined || svg.selectAll === undefined) {
					return chart;
				}

				updating = true;

				svg
					.selectAll('.tagText')
					.transition('tagcloud.text.greyout')
					.duration(options.transitionDuration)
					.style('fill', function(d) {
						return d.updateColor;
					})
					.on('start', function() {
						d3
							.select(this)
							.style('pointer-events', 'none');
					});
			} catch (err) {}

			return chart;
		};

		// define api for the module
		chart.api = {};
		chart.api.resetHover = chart.resetHover;

		return chart;
	};
}
